import { useCallback, useEffect, useRef, useState } from 'react'

import { useNavigate } from 'react-router-dom'
import i18n from 'src/i18n'

import { consumePostLoginRedirect } from 'src/app/auth/adapters/oauth-redirect/post-login-redirect-store'
import { clearSignInPending } from 'src/app/auth/adapters/oauth-redirect/sign-in-pending-store'
import type { OAuthProvider } from 'src/app/auth/domain/models/oauth-provider'
import type { User } from 'src/app/auth/domain/models/user'
import { usePolicyAcceptanceQuery } from 'src/app/auth/queries/policy-acceptance'
import { useCurrentUserQuery } from 'src/app/auth/repositories/use-current-user-query'
import { useHanwhaAccessQuery } from 'src/app/auth/repositories/use-hanwha-access-query'
import { useOAuthRedirect } from 'src/app/auth/repositories/use-oauth-redirect'
import { useAuthStore } from 'src/app/auth/store/auth-store'
import { Toast, toastTypes } from 'src/app/core/services/toast'

export type OAuthCallbackStatus =
  | 'pending'
  | 'fetching-user'
  | 'checking-access'
  | 'checking-terms'
  | 'redirecting'
  | 'failed'

export interface IUseOAuthCallbackReturn {
  status: OAuthCallbackStatus
}

interface ITransientCredentials {
  token: string
  provider: OAuthProvider
}

export const useOAuthCallback = (): IUseOAuthCallbackReturn => {
  const navigate = useNavigate()
  const setCurrentUser = useAuthStore(state => state.setCurrentUser)
  const setPendingToken = useAuthStore(state => state.setPendingToken)
  const oauthRedirect = useOAuthRedirect()
  const ranRef = useRef(false)
  const [status, setStatus] = useState<OAuthCallbackStatus>('pending')
  const [credentials, setCredentials] = useState<ITransientCredentials | null>(
    null,
  )
  const [verifiedUser, setVerifiedUser] = useState<User | null>(null)

  const failWith = useCallback(
    (params: { title: string; message: string }): void => {
      Toast.notify({
        title: params.title,
        message: params.message,
        type: toastTypes.ERROR,
      })
      setPendingToken(null)
      setStatus('failed')
      navigate('/login', { replace: true })
    },
    [navigate, setPendingToken],
  )

  useEffect(() => {
    if (ranRef.current) return
    ranRef.current = true
    clearSignInPending()

    const result = oauthRedirect.parseCallback(window.location.search)
    window.history.replaceState(null, '', window.location.pathname)
    if (result.status === 'error') {
      failWith({
        title: i18n.t('auth.login.errors.signinFailedTitle'),
        message: result.errorDetail ?? i18n.t('auth.login.errors.signinFailed'),
      })
      return
    }

    setPendingToken(result.accessToken)
    setCredentials({ token: result.accessToken, provider: result.provider })
    setStatus('fetching-user')
  }, [failWith, oauthRedirect, setPendingToken])

  const userQuery = useCurrentUserQuery({
    enabled: credentials !== null,
  })

  useEffect(() => {
    if (!credentials) return
    if (userQuery.isError) {
      failWith({
        title: i18n.t('auth.login.errors.signinFailedTitle'),
        message: i18n.t('auth.login.errors.signinFailed'),
      })
      return
    }
    if (userQuery.isSuccess && userQuery.data && !verifiedUser) {
      setVerifiedUser(userQuery.data)
      setStatus('checking-access')
    }
  }, [
    userQuery.isError,
    userQuery.isSuccess,
    userQuery.data,
    credentials,
    failWith,
    verifiedUser,
  ])

  const accessQuery = useHanwhaAccessQuery({
    uuid: verifiedUser?.uuid ?? null,
    enabled: verifiedUser !== null && !verifiedUser.isSuperAdmin,
  })

  useEffect(() => {
    if (!credentials || !verifiedUser) return
    if (verifiedUser.isSuperAdmin) {
      if (status !== 'checking-access') return
      setCurrentUser({
        token: credentials.token,
        data: verifiedUser,
        provider: credentials.provider,
      })
      setStatus('checking-terms')
      return
    }
    if (accessQuery.isError) {
      failWith({
        title: i18n.t('auth.login.errors.signinFailedTitle'),
        message: i18n.t('auth.login.errors.signinFailed'),
      })
      return
    }
    if (accessQuery.isSuccess && accessQuery.data) {
      if (!accessQuery.data.allowed) {
        failWith({
          title: i18n.t('auth.login.errors.notAuthorizedTitle'),
          message: i18n.t('auth.login.errors.notAuthorized'),
        })
        return
      }
      setCurrentUser({
        token: credentials.token,
        data: verifiedUser,
        provider: credentials.provider,
      })
      setStatus('checking-terms')
    }
  }, [
    accessQuery.isError,
    accessQuery.isSuccess,
    accessQuery.data,
    credentials,
    verifiedUser,
    failWith,
    setCurrentUser,
    status,
  ])

  const policyQuery = usePolicyAcceptanceQuery({
    enabled: status === 'checking-terms',
  })

  useEffect(() => {
    if (status !== 'checking-terms') return
    if (policyQuery.isError) {
      failWith({
        title: i18n.t('auth.login.errors.signinFailedTitle'),
        message: i18n.t('auth.login.errors.signinFailed'),
      })
      return
    }
    if (policyQuery.isSuccess) {
      setStatus('redirecting')
      if (policyQuery.data.accepted) {
        const redirectTo = consumePostLoginRedirect() ?? '/'
        navigate(redirectTo, { replace: true })
      } else {
        navigate('/terms', { replace: true })
      }
    }
  }, [
    status,
    policyQuery.isError,
    policyQuery.isSuccess,
    policyQuery.data,
    failWith,
    navigate,
  ])

  return { status }
}
